import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { ClientEntity } from './client.entity';
import { MSmClientOpportunityEntity } from './m-sm-client-opportunity.entity';
import { PriceContractEntity } from './price-contract.entity';
import { UserEntity } from './user.entity';

/**
 * Where a job stands. `closed` is set and cleared only by managers and admins
 * (`jobs:close`); the rest are ordinary edits.
 */
export const JOB_STATUSES = ['planned', 'active', 'on_hold', 'closed'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

/**
 * PD-17447 — how many levels a job tree may have, the top-level job counted
 * as the first (Hospital › Tower A › Floor 3 › Mechanical). The server refuses
 * a create, edit, move or merge that would go deeper (owner decision 5,
 * 10/24/26).
 */
export const JOB_TREE_MAX_DEPTH = 4;

/**
 * A job: the won work Workd's tenant delivers to one customer, with the
 * orders, quotes and invoices that belong to it (sales relationships plan,
 * section 4; PD-17093). Behind the `jobs` add-on capability.
 *
 * Jobs live in Workd (J1). An ERP job number or job name on an order is only
 * a suggestion; nothing creates a job automatically (J2). A job may come from
 * a won opportunity, and may name the job pricing contract it is priced under (J5: contracts are linked, never converted).
 *
 * Orders and quotes link through `order.fk_job__id`; invoices count through
 * their lines' orders. The quoted, ordered and invoiced totals are stored here
 * so the Jobs list can sort and filter on them, and are refreshed whenever a
 * link changes, when the job is opened, and nightly for open jobs.
 *
 * PD-17447 — a job may sit under a parent job (`fk_job__id_parent`), at most
 * JOB_TREE_MAX_DEPTH levels deep, with no loops (job-tree.utils.ts in the
 * jobs API). Every job keeps its own estimate and may hold orders of its own;
 * the stored totals come twice: for the job alone, and "with sub-jobs" (the
 * job and every job beneath it, each invoice counted once).
 *
 * SM2-only; SM1 never reads it.
 */
@Index('job__fk_client__id', ['fkClientId'], {})
@Index('job__fk_job__id_parent', ['fkJobIdParent'], {})
@Index('job__fk_user__id_salesperson', ['fkUserIdSalesperson'], {})
@Index('job__fk_m_sm_client_opportunity__id', ['fkOpportunityId'], {})
@Index('job__status', ['status'], {})
@Entity('job')
export class JobEntity extends BaseEntity {
	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	/** The ERP's job number or name, or one typed by the rep. Optional. */
	@Column('varchar', { name: 'jobNumber', length: 64, nullable: true })
	public jobNumber!: string | null;

	@Column('varchar', { name: 'status', length: 16, default: () => "'planned'" })
	public status!: JobStatus;

	@Column('text', { name: 'description', nullable: true })
	public description!: string | null;

	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	@Column('int', {
		name: 'fk_user__id_salesperson',
		unsigned: true,
		nullable: true,
	})
	public fkUserIdSalesperson!: number | null;

	@Column('date', { name: 'startDate', nullable: true })
	public startDate!: string | null;

	@Column('date', { name: 'endDate', nullable: true })
	public endDate!: string | null;

	@Column('decimal', {
		name: 'estimatedValue',
		precision: 13,
		scale: 2,
		nullable: true,
	})
	public estimatedValue!: string | null;

	/**
	 * PD-17830 — what the job's own opportunities are worth, refreshed with its
	 * totals. The job's estimate until one is entered; estimatedValue stays only
	 * what someone entered, so an edit never saves this as if they had typed it.
	 */
	@Column('decimal', {
		name: 'opportunityEstimate',
		precision: 13,
		scale: 2,
		nullable: true,
	})
	public opportunityEstimate!: string | null;

	/** The opportunity this job was won from, if any. */
	@Column('int', {
		name: 'fk_m_sm_client_opportunity__id',
		unsigned: true,
		nullable: true,
	})
	public fkOpportunityId!: number | null;

	/** The job pricing contract the job is priced under, if any (J5). */
	@Column('int', {
		name: 'fk_price_contract__id',
		unsigned: true,
		nullable: true,
	})
	public fkPriceContractId!: number | null;

	@Column('datetime', { name: 'closedDate', nullable: true })
	public closedDate!: Date | null;

	// Stored totals (see the class comment). Each order counts its whole value.
	@Column('decimal', {
		name: 'quotedTotal',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public quotedTotal!: string;

	@Column('decimal', {
		name: 'orderedTotal',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public orderedTotal!: string;

	@Column('decimal', {
		name: 'invoicedTotal',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public invoicedTotal!: string;

	/**
	 * PD-17135 — gross profit to date, ordered and invoiced, over the lines
	 * whose cost is known (the rule every sales report uses), and the margin
	 * as a 0–1 fraction of those lines' sales. NULL when no line has a known
	 * cost. Named so the GP response filter strips them for users without
	 * `orders:gp:view`.
	 */
	@Column('decimal', { name: 'orderedGP', precision: 13, scale: 2, nullable: true })
	public orderedGp!: string | null;

	@Column('decimal', { name: 'orderedGPMargin', precision: 9, scale: 6, nullable: true })
	public orderedGpMargin!: string | null;

	@Column('decimal', { name: 'invoicedGP', precision: 13, scale: 2, nullable: true })
	public invoicedGp!: string | null;

	@Column('decimal', { name: 'invoicedGPMargin', precision: 9, scale: 6, nullable: true })
	public invoicedGpMargin!: string | null;

	@Column('int', { name: 'quoteCount', unsigned: true, default: () => "'0'" })
	public quoteCount!: number;

	@Column('int', { name: 'orderCount', unsigned: true, default: () => "'0'" })
	public orderCount!: number;

	@Column('int', { name: 'invoiceCount', unsigned: true, default: () => "'0'" })
	public invoiceCount!: number;

	@Column('datetime', { name: 'lastOrderDate', nullable: true })
	public lastOrderDate!: Date | null;

	@Column('datetime', { name: 'totalsRefreshedDate', nullable: true })
	public totalsRefreshedDate!: Date | null;

	/**
	 * PD-17447 — the parent job, or null for a top-level job. Every existing
	 * job starts at the top; the column is additive and nothing is backfilled.
	 */
	@Column('int', { name: 'fk_job__id_parent', unsigned: true, nullable: true })
	public fkJobIdParent!: number | null;

	// PD-17447 — the stored totals "with sub-jobs": this job and every job
	// beneath it, refreshed with the job's own totals. An invoice whose lines
	// touch two of the jobs counts once. Stored, like the job's own totals, so
	// the Jobs list and the job reports can sort and filter on them.
	@Column('decimal', {
		name: 'estimatedValueWithSubJobs',
		precision: 13,
		scale: 2,
		nullable: true,
	})
	public estimatedValueWithSubJobs!: string | null;

	@Column('decimal', {
		name: 'quotedTotalWithSubJobs',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public quotedTotalWithSubJobs!: string;

	@Column('decimal', {
		name: 'orderedTotalWithSubJobs',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public orderedTotalWithSubJobs!: string;

	@Column('decimal', {
		name: 'invoicedTotalWithSubJobs',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public invoicedTotalWithSubJobs!: string;

	@Column('decimal', { name: 'orderedGPWithSubJobs', precision: 13, scale: 2, nullable: true })
	public orderedGpWithSubJobs!: string | null;

	@Column('decimal', { name: 'orderedGPMarginWithSubJobs', precision: 9, scale: 6, nullable: true })
	public orderedGpMarginWithSubJobs!: string | null;

	@Column('decimal', { name: 'invoicedGPWithSubJobs', precision: 13, scale: 2, nullable: true })
	public invoicedGpWithSubJobs!: string | null;

	@Column('decimal', { name: 'invoicedGPMarginWithSubJobs', precision: 9, scale: 6, nullable: true })
	public invoicedGpMarginWithSubJobs!: string | null;

	@Column('int', { name: 'quoteCountWithSubJobs', unsigned: true, default: () => "'0'" })
	public quoteCountWithSubJobs!: number;

	@Column('int', { name: 'orderCountWithSubJobs', unsigned: true, default: () => "'0'" })
	public orderCountWithSubJobs!: number;

	@Column('int', { name: 'invoiceCountWithSubJobs', unsigned: true, default: () => "'0'" })
	public invoiceCountWithSubJobs!: number;

	@Column('datetime', { name: 'lastOrderDateWithSubJobs', nullable: true })
	public lastOrderDateWithSubJobs!: Date | null;

	@ManyToOne(() => JobEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT', nullable: true })
	@JoinColumn([{ name: 'fk_job__id_parent', referencedColumnName: 'id' }])
	public parentJob!: JobEntity | null;

	@ManyToOne(() => ClientEntity, { onDelete: 'RESTRICT', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public client!: ClientEntity;

	@ManyToOne(() => UserEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_user__id_salesperson', referencedColumnName: 'id' }])
	public salesperson!: UserEntity | null;

	@ManyToOne(() => MSmClientOpportunityEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_m_sm_client_opportunity__id', referencedColumnName: 'id' },
	])
	public opportunity!: MSmClientOpportunityEntity | null;

	@ManyToOne(() => PriceContractEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_price_contract__id', referencedColumnName: 'id' }])
	public priceContract!: PriceContractEntity | null;
}
